import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import Swal from 'sweetalert2';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TarefasEmpresaService } from '../services/tarefas-empresa.service';
import { HomeService } from 'src/app/home/home-service/home.service';
import { ModulosBuscaService } from 'src/app/shared/components/filtros-busca/service/modulos-service/modulos.service';
import { FiltroSelectComponent, FiltroSelectOption } from 'src/app/shared/components/filtro-select/filtro-select.component';
import { FiltroSelectBuscaComponent } from 'src/app/shared/components/filtro-select-busca/filtro-select-busca.component';
import { FiltroBuscaTextoComponent } from 'src/app/shared/components/filtro-busca-texto/filtro-busca-texto.component';
import { PaginacaoTarefasComponent } from 'src/app/shared/components/paginacao-tarefas/paginacao-tarefas.component';
import { CommonLoaderComponent } from 'src/app/shared/components/common-loader/common-loader.component';
import { CriarTarefaEmpresaComponent } from 'src/app/shared/components/modais-tarefas/criar-tarefa-empresa/criar-tarefa-empresa.component';
import { FiltroPeriodoComponent } from 'src/app/metricas/components/filtros-metricas/filtro-periodo/filtro-periodo.component';
import { TarefaEmpresaRecord, TarefasEmpresaCounters } from '../interfaces/tarefas-empresa.interface';

interface EmpresaTarefas {
  companyId: number;
  companyTradeName: string;
  companyName: string;
  city: string;
  state: string;
  tarefas: TarefaEmpresaRecord[];
}

@Component({
  selector: 'app-tarefas-empresa',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    FiltroSelectComponent,
    FiltroSelectBuscaComponent,
    FiltroBuscaTextoComponent,
    FiltroPeriodoComponent,
    PaginacaoTarefasComponent,
    CommonLoaderComponent,
  ],
  templateUrl: './tarefas-empresa.component.html',
  styleUrls: ['./tarefas-empresa.component.scss'],
})
export class TarefasEmpresaComponent implements OnInit {
  totalTarefas = 0;
  totalCompanies = 0;
  counters: TarefasEmpresaCounters = { todas: 0, atrasadas: 0, pendentes: 0, concluidas: 0 };

  abaAtiva = 'todas';
  abas = [
    { key: 'todas', label: 'Todas as tarefas' },
    { key: 'atrasadas', label: 'Atrasadas' },
    { key: 'pendentes', label: 'Pendentes' },
    { key: 'concluidas', label: 'Concluídas' },
  ];

  filtros = {
    targetCompanyID: 0,
    responsibleID: 0,
    actionID: 0,
    moduleID: 0,
    dateBegin: null as string | null,
    dateEnd: null as string | null,
    situation: 'todas',
    name: null as string | null,
  };

  page = 1;
  pageSize = 50;
  pageLast = 1;
  recordsCount = 0;

  empresas: FiltroSelectOption[] = [{ value: 0, label: 'Todas as empresas' }];
  responsaveis: FiltroSelectOption[] = [{ value: 0, label: 'Todos os responsáveis' }];
  acoes: FiltroSelectOption[] = [{ value: 0, label: 'Todos os tipos de tarefa' }];
  modulos: FiltroSelectOption[] = [
    { value: 0, label: 'Todos os módulos' },
    { value: 234, label: 'Imobiliário' },
    { value: 5, label: 'Industrial' },
    { value: 6, label: 'Edificações' },
    { value: 7, label: 'Infraestrutura' },
  ];

  tarefas: TarefaEmpresaRecord[] = [];
  grupos: EmpresaTarefas[] = [];
  carregando = false;
  intervaloPeriodo: Date[] | null = null;

  private readonly _tarefasService = inject(TarefasEmpresaService);
  private readonly _homeService = inject(HomeService);
  private readonly _router = inject(Router);
  private readonly _modalService = inject(BsModalService);
  private modalRef?: BsModalRef;

  ngOnInit(): void {
    this.carregarCombos();
    this.carregarTarefas();
  }

  carregarCombos(): void {
    this._homeService.listResponsables().subscribe({
      next: (res: any) => {
        const lista = (res?.data ?? [])
          .map((r: any) => ({ value: Number(r.id), label: r.name }))
          .sort((a: FiltroSelectOption, b: FiltroSelectOption) =>
            a.label.localeCompare(b.label),
          );
        this.responsaveis = [{ value: 0, label: 'Todos os responsáveis' }, ...lista];
      },
      error: () => {},
    });

    this._tarefasService.listarAcoes().subscribe({
      next: (res: any) => {
        const lista = (res?.data ?? []).map((a: any) => ({ value: Number(a.actionId), label: a.description }));
        this.acoes = [{ value: 0, label: 'Todos os tipos de tarefa' }, ...lista];
      },
      error: () => {},
    });

    this._tarefasService.listTaskCompanies().subscribe({
      next: (res: any) => {
        const lista = (res?.data ?? []).map((e: any) => ({
          value: Number(e.companyId),
          label: e.tradeName || e.name,
        }));
        this.empresas = [{ value: 0, label: 'Todas as empresas' }, ...lista];
      },
      error: () => {},
    });
  }

  selecionarAba(aba: string): void {
    this.abaAtiva = aba;
    this.filtros.situation = aba;
    this.page = 1;
    this.carregarTarefas();
  }

  onFiltroChange(): void {
    this.page = 1;
    this.carregarTarefas();
  }

  onBuscaChange(nome: string): void {
    this.filtros.name = nome || null;
    this.page = 1;
    this.carregarTarefas();
  }

  onPeriodoChange(intervalo: { periodBegin: string; periodEnd: string } | null): void {
    this.filtros.dateBegin = intervalo?.periodBegin ?? null;
    this.filtros.dateEnd = intervalo?.periodEnd ?? null;
    this.page = 1;
    this.carregarTarefas();
  }

  get tagsFiltros(): { key: string; label: string }[] {
    const tags: { key: string; label: string }[] = [];

    const rotulo = (opcoes: FiltroSelectOption[], valor: number) =>
      opcoes.find((o) => o.value === valor)?.label ?? '';

    if (this.filtros.targetCompanyID) {
      tags.push({
        key: 'targetCompanyID',
        label: rotulo(this.empresas, this.filtros.targetCompanyID),
      });
    }
    if (this.filtros.responsibleID) {
      tags.push({
        key: 'responsibleID',
        label: rotulo(this.responsaveis, this.filtros.responsibleID),
      });
    }
    if (this.filtros.moduleID) {
      tags.push({ key: 'moduleID', label: rotulo(this.modulos, this.filtros.moduleID) });
    }
    if (this.filtros.actionID) {
      tags.push({ key: 'actionID', label: rotulo(this.acoes, this.filtros.actionID) });
    }
    if (this.filtros.dateBegin && this.filtros.dateEnd) {
      tags.push({
        key: 'periodo',
        label: `${this.formatarData(this.filtros.dateBegin)} - ${this.formatarData(this.filtros.dateEnd)}`,
      });
    }
    if (this.filtros.name) {
      tags.push({ key: 'name', label: `"${this.filtros.name}"` });
    }

    return tags;
  }

  get todosFiltrosSelecionados(): boolean {
    return (
      !!this.filtros.targetCompanyID &&
      !!this.filtros.responsibleID &&
      !!this.filtros.moduleID &&
      !!this.filtros.actionID &&
      !!this.filtros.dateBegin &&
      !!this.filtros.dateEnd &&
      !!this.filtros.name
    );
  }

  private formatarData(data: string): string {
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  removerFiltro(key: string): void {
    switch (key) {
      case 'periodo':
        this.filtros.dateBegin = null;
        this.filtros.dateEnd = null;
        this.intervaloPeriodo = null;
        break;
      case 'name':
        this.filtros.name = null;
        break;
      default:
        (this.filtros as any)[key] = 0;
    }

    this.page = 1;
    this.carregarTarefas();
  }

  limparFiltros(): void {
    this.filtros = {
      ...this.filtros,
      targetCompanyID: 0,
      responsibleID: 0,
      actionID: 0,
      moduleID: 0,
      dateBegin: null,
      dateEnd: null,
      name: null,
    };
    this.intervaloPeriodo = null;
    this.page = 1;
    this.carregarTarefas();
  }

  carregarTarefas(): void {
    this.carregando = true;

    const body = {
      ...this.filtros,
      pagination: { page: this.page, pageSize: this.pageSize, shortcuts: 10 },
    };

    this._tarefasService.listarTarefas(body).subscribe({
      next: (res: any) => {
        const data = res?.data ?? {};
        this.counters = data.counters ?? { todas: 0, atrasadas: 0, pendentes: 0, concluidas: 0 };
        this.totalCompanies = data.totalCompanies ?? 0;
        const paginacao = data.tasks ?? {};
        this.tarefas = paginacao.records ?? [];
        this.recordsCount = paginacao.recordsCount ?? 0;
        this.pageLast = paginacao.pageLast ?? 1;
        this.totalTarefas = this.counters[this.abaAtiva as keyof TarefasEmpresaCounters] ?? 0;
        this.agruparTarefas();
        this.carregando = false;
      },
      error: () => {
        this.tarefas = [];
        this.grupos = [];
        this.carregando = false;
      },
    });
  }

  agruparTarefas(): void {
    const mapa = new Map<number, EmpresaTarefas>();

    for (const tarefa of this.tarefas) {
      if (!mapa.has(tarefa.companyId)) {
        mapa.set(tarefa.companyId, {
          companyId: tarefa.companyId,
          companyTradeName: tarefa.companyTradeName,
          companyName: tarefa.companyName,
          city: tarefa.city,
          state: tarefa.state,
          tarefas: [],
        });
      }
      mapa.get(tarefa.companyId)!.tarefas.push(tarefa);
    }

    this.grupos = Array.from(mapa.values());
  }

  onPageChange(page: number): void {
    this.page = page;
    this.carregarTarefas();
  }

  abrirEmpresa(empresa: EmpresaTarefas): void {
    this._router.navigate(['/empresas/detalhes', 'imobiliario', empresa.companyId]);
  }

  iniciais(nome: string): string {
    return (nome ?? '').trim().substring(0, 2).toUpperCase();
  }

  classeStatus(status: string): string {
    const s = (status ?? '').toLowerCase();
    if (s.includes('atrasad')) return 'atrasada';
    if (s.includes('pendent')) return 'pendente';
    if (s.includes('conclu')) return 'concluida';
    return '';
  }

  concluirTarefa(tarefa: TarefaEmpresaRecord): void {
    this._tarefasService.alterarStatus(tarefa.taskId, 3).subscribe({
      next: () => this.carregarTarefas(),
      error: () => {},
    });
  }

  excluirTarefa(tarefa: TarefaEmpresaRecord): void {
    Swal.fire({
      title: 'Excluir tarefa',
      text: 'Deseja realmente excluir esta tarefa?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
    }).then((result) => {
      if (result.isConfirmed) {
        this._tarefasService.excluirTarefa(tarefa.taskId).subscribe({
          next: () => this.carregarTarefas(),
          error: (err) => Swal.fire('Erro', err?.error?.message ?? 'Não foi possível excluir a tarefa.', 'error'),
        });
      }
    });
  }

  editarTarefa(tarefa: TarefaEmpresaRecord): void {
    // TODO: abrir modal de edição
    console.log('Editar tarefa:', tarefa.taskId);
  }

  criarTarefa(): void {
    this.modalRef = this._modalService.show(CriarTarefaEmpresaComponent, { class: 'modal-md' });
    const componente = this.modalRef.content as CriarTarefaEmpresaComponent;
    componente.tarefaCriada.subscribe(() => {
      this.carregarTarefas();
      this.modalRef?.hide();
    });
    componente.fechar.subscribe(() => this.modalRef?.hide());
  }
}
